import { Code, ConnectError } from '@connectrpc/connect'
import { type TFunction } from 'i18next'

/**
 * spec AC-MSD-016 の「通信エラー・サーバーエラー」にあたる code。
 *
 * 通信断 (fetch 失敗) は connect-web が `Unknown` で返し、HTTP 5xx は
 * `Unavailable` / `Unknown` に写像されるため両方を含める。未認証・権限なし等は
 * 通信・サーバーエラーではないので含めず、従来の汎用文言 (`unknown`) に落とす。
 */
const TRANSPORT_OR_SERVER_ERROR_CODES: ReadonlySet<Code> = new Set([
  Code.Unknown,
  Code.Internal,
  Code.Unavailable,
  Code.DeadlineExceeded,
  Code.ResourceExhausted,
  Code.DataLoss,
  Code.Unimplemented,
])

/**
 * 特異日カレンダーの保存 (Create / Update) が失敗した時の表示文言。
 *
 * 業務エラーは code 単位の個別文言 (backend は構造化 detail を持たない)。
 * AlreadyExists は spec AC-MSD-013 (登録・編集で文言が異なる)。
 * 通信エラー・サーバーエラー (ConnectError 以外の例外を含む) は
 * spec AC-MSD-016「保存に失敗しました。時間をおいて再度お試しください」。
 */
export function mapSingularDaySaveError(
  err: unknown,
  t: TFunction,
  mode: 'create' | 'edit' = 'create',
): string {
  if (!(err instanceof ConnectError)) {
    return t('page:adminSingularDay.serverError.saveFailed')
  }
  switch (err.code) {
    case Code.AlreadyExists:
      // 登録は「エリアか日付」、 編集は日付を変えられないので「エリア」だけを案内する。
      return mode === 'edit'
        ? t('page:adminSingularDay.serverError.alreadyExistsEdit')
        : t('page:adminSingularDay.serverError.alreadyExistsCreate')
    case Code.Aborted:
      return t('page:adminSingularDay.serverError.aborted')
    case Code.NotFound:
      return t('page:adminSingularDay.serverError.notFound')
    case Code.InvalidArgument:
      return t('page:adminSingularDay.serverError.invalidArgument')
  }
  return TRANSPORT_OR_SERVER_ERROR_CODES.has(err.code)
    ? t('page:adminSingularDay.serverError.saveFailed')
    : t('page:adminSingularDay.serverError.unknown')
}

/**
 * 特異日の削除が失敗した時の表示文言。
 *
 * 削除で起こりうる業務エラーは楽観ロック衝突 (Aborted) と対象なし (NotFound)
 * だけなので、この 2 つだけ個別文言にする。通信エラー・サーバーエラー
 * (ConnectError 以外の例外を含む) は spec AC-MSD-016
 * 「削除に失敗しました。時間をおいて再度お試しください」。
 */
export function mapSingularDayDeleteError(err: unknown, t: TFunction): string {
  if (!(err instanceof ConnectError)) {
    return t('page:adminSingularDay.serverError.deleteFailed')
  }
  switch (err.code) {
    case Code.Aborted:
      return t('page:adminSingularDay.serverError.aborted')
    case Code.NotFound:
      return t('page:adminSingularDay.serverError.notFound')
  }
  return TRANSPORT_OR_SERVER_ERROR_CODES.has(err.code)
    ? t('page:adminSingularDay.serverError.deleteFailed')
    : t('page:adminSingularDay.serverError.unknown')
}
